import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useUserMetadata } from '../context/UserMetadataContext';
import Profile360Summary from './overview/Profile360Summary';
import AlertsList from './overview/AlertsList';
import { graphragService } from './Profile360/components/graphrag';
import { Box, Card, CardContent, Typography, Stack, TextField, InputAdornment, Chip, Button } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { StatCard } from './shared';

/* ── Shared section-header style ────────────────────────────────── */
const sectionTitle = {
  fontSize: '0.8rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'text.primary',
};

const Overview = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { isLoading: metadataLoading, error } = useUserMetadata();
  const navigate = useNavigate();

  const [starterSuggestions, setStarterSuggestions] = useState([]);
  const [graphragReady, setGraphragReady] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const token = await getAccessTokenSilently();
        graphragService.setAuthToken(token);
        setGraphragReady(true);

        const res = await graphragService.getStarterSuggestions();
        if (res.success && res.data?.suggestions) {
          setStarterSuggestions(res.data.suggestions);
        }
      } catch (err) {
        console.log('Could not init GraphRAG on overview:', err);
      }
    };

    if (isAuthenticated && !metadataLoading && !error) {
      init();
    }
  }, [isAuthenticated, metadataLoading, error, getAccessTokenSilently]);

  const handleQuerySubmit = (query) => {
    if (!query.trim()) return;
    navigate('/knowledge-graph/graphrag', { state: { initialQuery: query.trim() } });
  };

  if (error) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h5" color="error.main" gutterBottom>Access Denied</Typography>
          <Typography color="text.secondary">{error}</Typography>
        </Box>
      </Box>
    );
  }

  if (metadataLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <Box>
      {/* ── Row 1: Equal-width Stat Cards ─────────────────────── */}
      <Grid2 container spacing={2} sx={{ mb: 2 }}>
        <Grid2 xs={12} sm={6} md={3}>
          <StatCard
            label="Welcome back"
            value={new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            color="primary.main"
            subtitle={new Date().getFullYear().toString()}
          />
        </Grid2>
        <Grid2 xs={12} sm={6} md={3}>
          <StatCard label="Risk Score" value={72} color="warning.main" />
        </Grid2>
        <Grid2 xs={12} sm={6} md={3}>
          <StatCard label="Critical Alerts" value={14} color="error.main" trend="up" trendLabel="+3" />
        </Grid2>
        <Grid2 xs={12} sm={6} md={3}>
          <StatCard label="Compliance" value="94%" color="success.main" />
        </Grid2>
      </Grid2>

      {/* ── Row 2: Knowledge Graph ────────────────────────────── */}
      {graphragReady && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography sx={sectionTitle}>Profile360</Typography>
              <Button
                size="small"
                endIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                onClick={() => navigate('/knowledge-graph/graphrag')}
                sx={{ textTransform: 'none', color: 'primary.main', fontWeight: 500, fontSize: '0.8rem' }}
              >
                Open Explorer
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Ask questions about users, groups, access patterns, and risks
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Ask your knowledge graph..."
              onKeyDown={(e) => { if (e.key === 'Enter') handleQuerySubmit(e.target.value); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.subtle',
                },
              }}
            />
            {starterSuggestions.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {starterSuggestions.map((s, i) => (
                  <Chip
                    key={i}
                    label={typeof s === 'string' ? s : s.text || s.label || String(s)}
                    variant="outlined"
                    size="small"
                    onClick={() => handleQuerySubmit(typeof s === 'string' ? s : s.text || s.label || String(s))}
                    sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                  />
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Row 3: Alerts + Risk Overview (equal 6/6 split) ──── */}
      <Grid2 container spacing={2}>
        <Grid2 xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography sx={sectionTitle}>Active Alerts</Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/profile360/analytics')}
                  sx={{ textTransform: 'none', color: 'primary.main', fontWeight: 500, fontSize: '0.8rem' }}
                >
                  View All &rarr;
                </Button>
              </Stack>
              <AlertsList />
            </CardContent>
          </Card>
        </Grid2>
        <Grid2 xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography sx={sectionTitle}>Risk Overview</Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/profile360/analytics')}
                  sx={{ textTransform: 'none', color: 'primary.main', fontWeight: 500, fontSize: '0.8rem' }}
                >
                  Full Dashboard &rarr;
                </Button>
              </Stack>
              <Profile360Summary />
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default Overview;
